<script setup lang="ts" generic="TOption extends object | string | number, TValue extends any = TOption">
import {
  computed,
  h,
  isRef,
  toRaw,
  type Component,
  type MaybeRef,
} from 'vue';
import Checkbox from 'primevue/checkbox';
import Chip from 'primevue/chip';
import Select, { type SelectProps } from 'primevue/select';
import { useUtilities } from '@apparatix/composables';
import { Toolbar, Typography } from './core';
import RadioFor from './RadioFor.vue';

// This is the no break space char, the same as &nbsp;
const NBSP = '\u00A0';

type Props = Omit<SelectProps, 'multiple'> & {
  filterFields?: TOption extends object ? Array<keyof TOption> : never,
  maxSelectionsShown?: number,
  optionLabel?: TOption extends object ? keyof TOption | ((option: TOption) => string) : never,
  options: TOption[],
  optionValue?: TOption extends object ? keyof TOption | ((option: TOption) => any) : never,
  selectedLabel?: ((selected?: TValue[]) => string) | MaybeRef<string>,
  selectionLimit?: number,
  showChips?: boolean,
  showToggleAll?: boolean,
  toggleAllLabel?: string,
  wrapChips?: boolean,
};

const props = withDefaults(defineProps<Props>(), {
  filterFields: undefined,
  maxSelectionsShown: undefined,
  optionLabel: undefined,
  optionValue: undefined,
  selectedLabel: undefined,
  selectionLimit: undefined,
  showToggleAll: true,
  toggleAllLabel: undefined,
});

const model = defineModel<TValue[]>({ default: []});

const {
  areObjectsEqual,
  arrayHasValues,
  developerWarning,
  isFunction,
  isObject,
  isValidNumber,
  omit,
  removeArrayElement,
} = useUtilities();

developerWarning(() => Boolean(props.inputId), new Error('The `inputId` prop is deprecated. Use `labelId` instead'), 'throw');
developerWarning(() => Boolean(props.inputStyle), new Error('The `inputStyle` prop is deprecated. Use `labelStyle` instead'), 'throw');
developerWarning(() => Boolean(props.inputClass), new Error('The `inputClass` prop is deprecated. Use `labelClass` instead'), 'throw');

const isAllSelected = computed(() => {
  if (model.value === undefined || model.value === null || props.options === undefined) {
    return false;
  }

  return Array.isArray(model.value) && model.value.length === props.options.length && arrayHasValues(props.options);
});

const selectPassthroughProps = computed<SelectProps>(() => {
  const filteredProps = omit(props, [
    'maxSelectionsShown',
    'showChips',
    'showToggleAll',
    'selectionLimit',
    'toggleAllLabel',
  ]);

  return {
    ...filteredProps,
    // The multiple prop is forced because not allowing multiple makes no sense and would break the component. Just use a stock Primevue Select if you don't want to allow multiple
    multiple: true,
  };
});

const isIndeterminate = computed(() => arrayHasValues(model) && model.value?.length < props.options?.length);

const maxShownValues = computed(() => {
  if (props.maxSelectionsShown) return props.maxSelectionsShown;

  if (!props.showChips || !props.wrapChips) {
    return 3;
  }

  return Number.MAX_SAFE_INTEGER;
});

const getOptionLabel = (option: TOption | TValue): string => {
  if (isObject(option) && props.optionLabel) {
    return isFunction(props.optionLabel)
      ? props.optionLabel(option)
      : String(option[props.optionLabel]);
  }

  return String(option);
};

const getOptionValue = (option: TOption): TValue => {
  if (isObject(option)) {
    if (props.optionValue) {
      return isFunction(props.optionValue)
        ? props.optionValue(option)
        : option[props.optionValue];
    }
  }

  return option as unknown as TValue;
};

const valuesMatch = (a: TValue, b: TValue, useDataKey: boolean = true): boolean => {
  if (typeof a !== typeof b) return false;

  if (isObject(a)) {
    if (useDataKey && props.dataKey && (props.dataKey in a) && (props.dataKey in (b as object))) {
      return valuesMatch(a[props.dataKey], b[props.dataKey], false);
    }

    return areObjectsEqual(
      toRaw(a),
      toRaw(b as object),
    );
  }

  return a === b;
};

const findOriginalOption = (value: TValue): TOption | undefined => props.options?.find(opt => valuesMatch(getOptionValue(opt), value));

const getSelectedItemLabel = (item: TValue): string => {
  if (!props.optionValue) {
    return getOptionLabel(item);
  }

  const option = findOriginalOption(item);

  return option ? getOptionLabel(option) : String(item);
};

const ValueLabel = computed<Component>(() => {
  if (props.selectedLabel) {
    const labelValue: string = isFunction(props.selectedLabel)
      ? props.selectedLabel(model.value)
      : isRef(props.selectedLabel)
        ? props.selectedLabel.value
        : props.selectedLabel;

    return h('span', labelValue);
  }

  if (!arrayHasValues(model)) {
    return h('span', { class: 'p-select-placeholder' }, props.placeholder ?? NBSP);
  }

  if (arrayHasValues(model, { exactly: 1 })) {
    return h('span', getSelectedItemLabel(model.value[0]));
  }

  const [shownItems, hiddenCount] = splitVisibleAndHiddenValues();

  if (hiddenCount > 0) {
    return h('span', [
      `${shownItems.map(item => getSelectedItemLabel(item)).join(', ')}`,
      h('span', { style: { color: 'var(--p-select-placeholder-color)' }}, ` +${hiddenCount} more`),
    ]);
  }

  return h('span', shownItems.map(item => getSelectedItemLabel(item)).join(', '));
});

const shouldShowSelectAll = computed<boolean>(() => {
  if (!props.showToggleAll) return false;

  if (props.selectionLimit && arrayHasValues(props.options) && props.options.length > props.selectionLimit) {
    developerWarning(
      true,
      `MultiSelect hid the toggle all control because the provided selection limit of ${props.selectionLimit} is less than the provided number of options (${props.options?.length ?? 0})`,
      'console:warn',
    );

    return false;
  }

  return true;
});

const valuesForChips = computed<{ items: Array<{ label: string, item: TValue }>, hiddenCount: number }>(() => {
  const [shownItems, hiddenCount] = splitVisibleAndHiddenValues();

  return {
    items: shownItems.map(item => ({ label: getSelectedItemLabel(item), item })),
    hiddenCount,
  };
});

const isSelectionLimitReached = (): boolean => props.selectionLimit
  ? isValidNumber(props.selectionLimit) && arrayHasValues(model, { min: 0, max: props.selectionLimit })
  : false;

const splitVisibleAndHiddenValues = (): [visible: TValue[], hiddenCount: number] => {
  if (!arrayHasValues(model)) {
    return [[], 0];
  }

  const shown = model.value.slice(0, maxShownValues.value);

  if (shown.length >= model.value.length) {
    return [shown, 0];
  }

  const hidden = model.value.slice(maxShownValues.value);

  return [shown, hidden.length];
};

const commitSelection = (newValue: TValue[]): void => {
  const isAddition = newValue.length > (model.value?.length ?? 0);

  if (isAddition && props.selectionLimit !== undefined && newValue.length > props.selectionLimit) {
    return;
  }

  model.value = newValue;
};

const toggleAll = () => commitSelection(
  isAllSelected.value ? [] : props.options.map(opt => getOptionValue(opt)),
);

const isOptionSelected = (option: TOption): boolean => {
  const optionValue = getOptionValue(option);

  return Boolean(model.value?.some(modelValue => valuesMatch(modelValue, optionValue)));
};

const onChipItemRemove = (e: Event, item: TValue): void => {
  e.preventDefault();

  removeArrayElement(model, i => i === item);
};

const shouldDisableOption = (option: TOption): boolean => {
  if (isSelectionLimitReached() && !isOptionSelected(option)) {
    return true;
  }

  if (isFunction(props.optionDisabled)) {
    return props.optionDisabled(option);
  }

  return false;
};
</script>
<template>
  <Select :model-value="model" v-bind="{...selectPassthroughProps, ...$attrs}" @update:model-value="commitSelection">
    <template v-if="shouldShowSelectAll" #header>
      <Toolbar align="left" class="p-[var(--p-select-list-padding)]">
        <RadioFor>
          <template #default="{id}">
            <div class="inline-block p-[var(--p-select-option-padding)] pr-0">
              <Checkbox
                :aria-label="props.toggleAllLabel ? props.toggleAllLabel : 'Toggle all'"
                :disabled="!arrayHasValues(props.options) || props.disabled"
                :indeterminate="isIndeterminate"
                :input-id="id"
                :model-value="isAllSelected"
                binary
                @update:model-value="toggleAll"
              />
            </div>
          </template>
          <template v-if="props.toggleAllLabel" #label="{id}">
            <Typography variant="form-label" class="pt-[0.25rem]" :for="id">
              {{ props.toggleAllLabel }}
            </Typography>
          </template>
        </RadioFor>
      </Toolbar>
    </template>
    <template #value>
      <ValueLabel v-if="!props.showChips" />
      <div v-else class="flex items-center gap-1" :class="{'flex-wrap': props.wrapChips}">
        <template v-if="!$slots.chips">
          <template v-if="valuesForChips.items.length">
            <Chip
              v-for="item in valuesForChips.items"
              :key="item.label"
              :label="item.label"
              :removable="!props.disabled"
              @remove="e => onChipItemRemove(e, item.item)"
            />
            <span v-if="valuesForChips.hiddenCount > 0">+{{ valuesForChips.hiddenCount }} more</span>
          </template>
          <span v-if="!valuesForChips.items.length">{{ props.placeholder ?? NBSP }}</span>
        </template>
        <template v-else>
          <template v-if="arrayHasValues(model)">
            <slot v-for="item in model" name="chips" :value="(item as TValue)" />
          </template>
          <span v-else>{{ props.placeholder ?? NBSP }}</span>
        </template>
      </div>
    </template>
    <template #option="slotProps">
      <div class="flex items-center gap-2">
        <Checkbox
          :model-value="isOptionSelected(slotProps.option)"
          :tabindex="-1"
          :disabled="shouldDisableOption(slotProps.option) || props.disabled"
          binary
          readonly
        />
        <span>{{ getOptionLabel(slotProps.option) }}</span>
      </div>
    </template>
  </Select>
</template>
